/**
 * /university/ab-tests/new — start a new A/B experiment.
 *
 * Simple form (founder + tuner only):
 *   - Pick a skill from the catalog.
 *   - Fill candidate version label.
 *   - Provide candidate prompt text (text-area).
 *   - Set sample window (duration_days) + traffic split.
 *   - Optionally "dry-run" — preview without persisting.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, FlaskConical, Loader2 } from 'lucide-react';
import * as React from 'react';

import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { useToaster } from '../../../../components/ui/toast';
import { learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../stores/auth-store';

function AbTestsNewRoute() {
  const navigate = useNavigate();
  const { toast } = useToaster();

  const skillsQ = useQuery({
    queryKey: learningKeys.skillsList({}),
    queryFn: () => learningApi.listSkills({}),
  });

  const [skillId, setSkillId] = React.useState<string>('');
  const [candidateVersion, setCandidateVersion] = React.useState<string>('');
  const [candidatePromptText, setCandidatePromptText] = React.useState<string>('');
  const [trafficSplit, setTrafficSplit] = React.useState<number>(0.2);
  const [durationDays, setDurationDays] = React.useState<number>(14);
  const [dryRun, setDryRun] = React.useState<boolean>(false);

  const selectedSkill = React.useMemo(
    () => skillsQ.data?.skills.find((s) => s.skillId === skillId) ?? null,
    [skillsQ.data, skillId],
  );

  // Auto-derive a default candidate version from the active skill version.
  React.useEffect(() => {
    if (!selectedSkill) return;
    if (candidateVersion) return;
    const parts = selectedSkill.version.split('.').map((p) => parseInt(p, 10));
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      const [maj, min, patch] = parts;
      const major = Number(maj);
      const minor = Number(min);
      const patchN = Number(patch);
      if (Number.isFinite(major) && Number.isFinite(minor) && Number.isFinite(patchN)) {
        setCandidateVersion(`${major}.${minor}.${patchN + 1}-candidate`);
      }
    } else {
      setCandidateVersion(`${selectedSkill.version}-candidate`);
    }
  }, [selectedSkill, candidateVersion]);

  const createM = useMutation({
    mutationFn: () =>
      learningApi.createAbTest({
        skillId,
        controlVersion: selectedSkill?.version ?? '',
        candidateVersion,
        candidatePromptText,
        durationDays,
        trafficSplit,
        dryRun,
      }),
    onSuccess: (test) => {
      if (dryRun) {
        toast({
          title: 'Dry-run preview',
          description: `${candidateVersion} would run for ${durationDays} days at ${(trafficSplit * 100).toFixed(0)}%.`,
        });
        return;
      }
      toast({
        title: 'A/B test created',
        description: `Now monitoring ${test.candidateVersion}.`,
      });
      void navigate({ to: '/university/ab-tests/$testId', params: { testId: test.id } });
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not create A/B test',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const canSubmit =
    skillId.length > 0 &&
    candidateVersion.trim().length > 0 &&
    candidatePromptText.trim().length >= 8 &&
    selectedSkill !== null &&
    !createM.isPending;

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <Link
        to="/university/ab-tests"
        className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-3 w-3" aria-hidden /> Back to A/B tests
      </Link>

      <header className="flex items-center gap-2">
        <FlaskConical className="h-4 w-4 text-foreground" aria-hidden />
        <h1 className="text-[16px] font-semibold tracking-tight text-foreground">New A/B test</h1>
      </header>

      <form
        className="flex flex-col gap-4 rounded border border-border bg-background p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) createM.mutate();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="abt-skill" className="text-[12px]">
            Skill
          </Label>
          <select
            id="abt-skill"
            value={skillId}
            onChange={(e) => {
              setSkillId(e.target.value);
              setCandidateVersion(''); // re-derive
            }}
            className={cn(
              'h-9 rounded border border-border bg-background px-2 text-[13px]',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            )}
            disabled={skillsQ.isPending}
          >
            <option value="">Select a skill…</option>
            {(skillsQ.data?.skills ?? []).map((s) => (
              <option key={s.skillId} value={s.skillId}>
                {s.name} (v{s.version})
              </option>
            ))}
          </select>
          {selectedSkill ? (
            <span className="text-[11px] text-muted-foreground">
              Control version <span className="font-mono">{selectedSkill.version}</span>
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="abt-cand-version" className="text-[12px]">
            Candidate version label
          </Label>
          <Input
            id="abt-cand-version"
            value={candidateVersion}
            onChange={(e) => setCandidateVersion(e.target.value)}
            placeholder="e.g. 4.3.0-candidate"
            className="h-9 font-mono text-[13px]"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="abt-prompt" className="text-[12px]">
            Candidate prompt text
          </Label>
          <textarea
            id="abt-prompt"
            value={candidatePromptText}
            onChange={(e) => setCandidatePromptText(e.target.value)}
            rows={10}
            placeholder="Write the new prompt template here. At least 8 characters."
            className={cn(
              'rounded border border-border bg-background p-2 font-mono text-[12px] text-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            )}
          />
          <span className="text-[11px] text-muted-foreground">
            {candidatePromptText.length} chars
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="abt-split" className="text-[12px]">
              Traffic split (candidate fraction)
            </Label>
            <Input
              id="abt-split"
              type="number"
              min={0.05}
              max={0.95}
              step={0.05}
              value={trafficSplit}
              onChange={(e) => setTrafficSplit(Number(e.target.value))}
              className="h-9 text-[13px]"
            />
            <span className="text-[11px] text-muted-foreground">
              {(trafficSplit * 100).toFixed(0)}% of dispatches route to the candidate.
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="abt-days" className="text-[12px]">
              Duration (days)
            </Label>
            <Input
              id="abt-days"
              type="number"
              min={1}
              max={180}
              step={1}
              value={durationDays}
              onChange={(e) => setDurationDays(Number(e.target.value))}
              className="h-9 text-[13px]"
            />
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-[12px] text-muted-foreground">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
          Dry-run preview (don't persist; show what would happen)
        </label>

        <footer className="flex items-center justify-end gap-2">
          <Button asChild type="button" variant="outline">
            <Link to="/university/ab-tests">Cancel</Link>
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {createM.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                {dryRun ? 'Previewing…' : 'Creating…'}
              </>
            ) : dryRun ? (
              'Preview dry-run'
            ) : (
              'Start A/B test'
            )}
          </Button>
        </footer>
      </form>
    </section>
  );
}

export const Route = createFileRoute('/_authenticated/university/ab-tests/new')({
  beforeLoad: () => {
    const state = useAuthStore.getState();
    const role =
      state.workspaces.find((w) => w.workspace.id === state.activeWorkspaceId)?.membership.role ??
      null;
    if (role !== 'founder' && role !== 'admin' && role !== 'tuner') {
      throw redirect({ to: '/university/ab-tests' });
    }
  },
  component: AbTestsNewRoute,
});
